import { useEffect, useState } from 'react';
import { Button } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import useVoucherViewer from '../hooks/useVoucherViewer';
import { Chip, Skeletons, RM } from './kit';
import { shortAddress } from '../lib/format';
import { commissionTypeLabel } from '../lib/commission';
import { isReversed, payoutDate, payoutDeductions, payoutNet } from '../lib/payout';
import * as earnApi from '../api/earnings';

const label = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14 };
const mono = { fontFamily: 'ui-monospace,monospace' };

/** The small red "PDF" a voucher with a file carries wherever its number is shown. */
export const PdfMark = () => (
  <span style={{ color: '#cf1322', fontWeight: 700, fontSize: 10, flex: 'none' }}>PDF</span>
);

/**
 * A voucher number as one inline line: the PDF mark when there is a file to
 * open, the number struck through when the voucher was voided with its payout.
 * Old vouchers carried over from SQL Accounting are a number only — no mark.
 */
export function VoucherRef({ voucher, color, style }) {
  if (!voucher?.doc_no) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, ...style }}>
      {voucher.has_file && <PdfMark />}
      <span style={{ ...mono, color, textDecoration: voucher.voided ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {voucher.doc_no}
      </span>
    </span>
  );
}

/**
 * Tapping a payout on Earnings — or a "Payout paid" / "Payment voucher ready"
 * notification, which deep-links to /earnings?payout=<id> — opens this: what the
 * payout paid, and the payment voucher HQ issued for it.
 *
 * A bottom sheet like CommissionSheet, for the same reason: the list stays behind
 * it, so checking one payout costs no navigation. `row` is the list row when the
 * sheet was opened from the list, so the header draws at once; from a deep link
 * there is no row and the header waits on the detail read like the rest.
 */
export default function PayoutSheet({ open, payoutId: idProp, row, onClose }) {
  const { mounted, closing } = useSheetAnim(open);
  // Closing clears the id upstream (it lives in the URL) while the exit animation
  // is still playing, so the sheet keeps drawing the last payout it was given.
  const [payoutId, setPayoutId] = useState(idProp);
  if (idProp && idProp !== payoutId) setPayoutId(idProp);
  const pv = useVoucherViewer();
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open || !payoutId) return undefined;
    let live = true;
    setD(null);
    setFailed(null);
    earnApi.getEarningsPayout(payoutId)
      .then((r) => { if (live) setD(r?.data ?? r); })
      .catch((e) => { if (live) setFailed(e); });
    return () => { live = false; };
  }, [open, payoutId, tick]);

  if (!mounted) return null;

  // The detail wins once it lands — it is the fresher read of the same row.
  const p = d || (row?.id === payoutId ? row : null);
  const reversed = isReversed(p);
  const voucher = p?.voucher || null;
  const lines = Array.isArray(d?.lines) ? d.lines : [];
  const notFound = failed?.status === 404;

  return (
    <>
      <div
        onClick={onClose}
        className={`sheet-scrim${closing ? ' is-closing' : ''}`}
        style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`sheet-panel${closing ? ' is-closing' : ''}`}
          style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '86%', display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ flex: 'none', padding: '10px 16px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 13px' }} />
            {!p ? (
              notFound || failed ? (
                <div style={label}>PAYOUT</div>
              ) : <Skeletons n={1} height={58} />
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <span style={label}>PAYOUT · {payoutDate(p.payout_date).toUpperCase()}</span>
                  {reversed && <Chip label="Reversed" tone="bad" />}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: reversed ? 'var(--muted)' : 'var(--ok)', textDecoration: reversed ? 'line-through' : 'none' }}>
                  {RM(payoutNet(p))}
                </div>
                {/* The clawback HQ recovered out of this payout is the gap between
                    what was released and what reached the bank, so it is spelt out
                    rather than left as a figure that does not match the lines. */}
                {payoutDeductions(p, RM) && (
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {payoutDeductions(p, RM)}
                  </div>
                )}
                {p.channel && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>Paid by {p.channel}</div>}
              </>
            )}
          </div>

          <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {notFound ? (
              <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>This payout could not be found.</div>
            ) : (
              <>
                {reversed && (
                  <div style={{ background: 'color-mix(in srgb,var(--bad) 7%,var(--surface))', border: '1px solid color-mix(in srgb,var(--bad) 28%,var(--border))', borderRadius: 12, padding: '13px 15px' }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--bad)' }}>
                      REVERSED{p.reversed_at ? ` · ${payoutDate(String(p.reversed_at).slice(0, 10)).toUpperCase()}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginTop: 3 }}>
                      HQ took this payout back, so it no longer counts as paid
                      {d?.reversal_reason ? <>. Reason: <span style={{ color: 'var(--text)' }}>{d.reversal_reason}</span></> : '.'}
                    </div>
                  </div>
                )}

                <div style={label}>WHAT IT PAID</div>
                {failed ? (
                  <div style={{ ...card, marginTop: -4, padding: '13px 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Couldn&rsquo;t load this payout just now.</span>
                    <Button size="small" onClick={() => setTick((t) => t + 1)}>Try again</Button>
                  </div>
                ) : !d ? (
                  <Skeletons n={2} height={54} />
                ) : lines.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>No sale lines are recorded against this payout.</div>
                ) : (
                  <div style={{ ...card, marginTop: -4, overflow: 'hidden' }}>
                    {lines.map((l, i) => (
                      <div key={`${l.sale_id}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '13px 15px', borderBottom: i < lines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, ...mono }}>{l.sale_ref || '—'}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {[commissionTypeLabel(l.commission_type), shortAddress(l.property)].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{RM(l.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {p && (
                  <>
                    <div style={label}>PAYMENT VOUCHER</div>
                    {voucher?.doc_no ? (
                      <div style={{ ...card, marginTop: -4, padding: '13px 15px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ width: 34, height: 34, borderRadius: 9, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: voucher.has_file ? 'color-mix(in srgb,#cf1322 10%,var(--surface))' : 'var(--surface2)', color: voucher.has_file ? '#cf1322' : 'var(--muted)', fontSize: 10, fontWeight: 800 }}>
                          {voucher.has_file ? 'PDF' : 'PV'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, ...mono, textDecoration: voucher.voided ? 'line-through' : 'none' }}>{voucher.doc_no}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                            {voucher.voided
                              ? 'Voided — the payout was reversed'
                              : voucher.source === 'SQL' ? 'Posted to SQL Accounting by HQ finance' : voucher.has_file ? 'Issued by HQ finance' : 'Issued by HQ finance · number only, no copy in CIDAS'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ ...card, marginTop: -4, padding: '13px 15px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                        {reversed
                          ? 'No payment voucher was attached before HQ reversed this payout.'
                          : 'HQ has not attached the voucher yet — you’ll get a notification when it’s ready.'}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div style={{ padding: '15px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
            {voucher?.has_file ? (
              <Button
                type="primary"
                block
                loading={pv.busy === payoutId}
                onClick={() => pv.open(payoutId)}
                style={{ height: 46, fontWeight: 600 }}
              >
                View payment voucher
              </Button>
            ) : (
              <Button block onClick={onClose}>Close</Button>
            )}
          </div>
        </div>
      </div>
      {pv.viewer}
    </>
  );
}
